import { forwardRef, useId, useState, type DragEvent } from "react";
import {
  FileUploaderStatus,
  type FileUploaderProps,
} from "./FileUploader.types";
import FileItem from "./FileItem";
import type { FileItemType } from "$/lib/types/FileUploader.types";
import { cn } from "$/lib/utils/functions/misc.functions";
import FileUploaderContent from "./FileUploaderContent";
import FileUploaderHeader from "./FileUploaderHeader";
import { generateRandomId } from "$/lib/utils/functions/util.functions";

const FileUploader = forwardRef<HTMLInputElement, FileUploaderProps>(
  (
    {
      name,
      className,
      files: initialFiles = [],
      onChange = () => undefined,
      onDragChange = () => undefined,
      onCancel = () => undefined,
      onDelete = () => undefined,
    },
    ref,
  ) => {
    const [dragging, setDragging] = useState(false);
    const [files, setFiles] = useState<FileItemType[]>(
      initialFiles.map((file) =>
        Object.assign(file, {
          id: file.id || generateRandomId(),
        }),
      ),
    );

    const id = useId();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) return;

      setFiles((prev) => [
        ...prev,
        ...Array.from(event.target.files || []).map((file) =>
          Object.assign(file, {
            id: generateRandomId(),
          }),
        ),
      ]);

      onChange(event);
    };

    const _handleRemove = (file: FileItemType) => {
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
    };

    const handleCancel = (file: FileItemType) => {
      _handleRemove(file);
      onCancel(file);
    };

    const handleDelete = (file: FileItemType) => {
      _handleRemove(file);
      onDelete(file);
    };

    const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
      setDragging(false);
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        setFiles((prev) => [
          ...prev,
          ...Array.from(files || []).map((file) =>
            Object.assign(file, {
              id: generateRandomId(),
            }),
          ),
        ]);

        onDragChange(e);
      }
    };

    return (
      <div
        className={cn(
          "flex w-full flex-col gap-6 rounded-2xl border bg-white p-5",
          className,
        )}
      >
        <FileUploaderHeader />
        <label
          onDragEnter={handleDragOver}
          onDrop={handleDrop}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onDragLeave={handleDragLeave}
          htmlFor={id}
          className="flex cursor-pointer flex-col items-center justify-center gap-8 rounded-2xl border-4 border-dashed px-4 py-12"
        >
          <input
            id={id}
            name={name}
            className="hidden"
            type="file"
            onChange={handleChange}
            multiple
            ref={ref}
          />
          <FileUploaderContent dragging={dragging} />
        </label>
        {files.map((file) => (
          <FileItem
            status={FileUploaderStatus.UPLOADING}
            key={file.id}
            file={file}
            currentUploadedSize={file.size / 4}
            onCancel={() => handleCancel(file)}
            onDelete={() => handleDelete(file)}
          />
        ))}
      </div>
    );
  },
);

export default FileUploader;
